/**
 * dataCache.js
 *
 * Cache-first-then-revalidate helpers built on the IndexedDB wrapper in
 * browserDb.js.  Two access patterns are supported:
 *
 *  1. Collections (cachedCollection) — datasets of independently-keyed
 *     entities (tracking runs, CP survey jobs, favourites).  When the backend
 *     supports it, only the delta (records changed since the last sync) is
 *     downloaded and merged into the cached collection; otherwise the full
 *     list is downloaded and replaces the cache.  Either way the caller is
 *     handed the cached copy immediately (synchronously-ish, via onCached)
 *     and then the freshened copy when the network resolves.
 *
 *  2. Queries (cachedQuery) — opaque request/response pairs for endpoints
 *     where per-item delta merging does not apply (server-side pagination,
 *     map-extent filters, full-text search).  These are cached with a TTL and
 *     served stale-while-revalidate.
 *
 * The cache is scoped per user so one account never sees another's data.
 */

import {
	STORE_COLLECTIONS,
	STORE_QUERIES,
	dbGet,
	dbPut,
	dbDelete,
	dbRowsWithPrefix,
	dbClearAll
} from './browserDb';
import { getAccessToken } from './token';
import { clearPageCache } from '../../commons/util/pageCache';

// ── User scoping ──────────────────────────────────────────────────

/**
 * Derive a short, stable id for the logged-in user from the JWT so cached
 * data is partitioned per account.  Falls back to 'anon' when no token.
 *
 * We hash the token's `sub`/`username` claim rather than store the raw token.
 */
const decodeJwtPayload = token => {
	try {
		const payload = token.split('.')[1];
		const json = decodeURIComponent(
			window.atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
				.split('')
				.map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
				.join('')
		);
		return JSON.parse(json);
	} catch (err) {
		return null;
	}
};

export const getCacheUserId = () => {
	const token = getAccessToken();
	if (!token) {return 'anon';}
	const payload = decodeJwtPayload(token);
	const id =
		(payload && (payload.sub || payload.username || payload['cognito:username'])) ||
		null;
	return id ? String(id) : 'anon';
};

/** Build a fully-qualified, user-scoped cache key. */
export const cacheKey = (...parts) =>
	[getCacheUserId(), ...parts.map(p => String(p))].join('::');

/**
 * Deterministically serialise a request object into a compact key fragment so
 * two equivalent requests (regardless of property insertion order) map to the
 * same cache entry.  Arrays preserve order; object keys are sorted.
 */
export const stableKey = value => {
	if (value === null || value === undefined) {return '';}
	if (Array.isArray(value)) {
		return `[${value.map(stableKey).join(',')}]`;
	}
	if (typeof value === 'object') {
		return `{${Object.keys(value)
			.sort()
			.map(k => `${k}:${stableKey(value[k])}`)
			.join(',')}}`;
	}
	return String(value);
};

// ── Time helpers ──────────────────────────────────────────────────

const now = () => Date.now();

/**
 * Canonicalise any timestamp-ish value to the ISO-8601 string the backend
 * sends and expects back as `updatedSince`.
 *
 * Why this exists: `lastSync` used to be stored as whatever produced it -- an
 * epoch-milliseconds Number when it was derived locally from the items, an ISO
 * string when the server supplied one.  The next delta then posted a Number
 * where the API contract says string; the backend read that as "no watermark"
 * and answered with a delta scoped differently from the collection it was
 * merged into.  One canonical type removes that whole class of drift.
 *
 * @returns {string|null} ISO-8601 string, or null when unparseable.
 */
export const toIsoTimestamp = value => {
	if (value === null || value === undefined || value === '') {return null;}
	const ms = typeof value === 'number' ? value : Date.parse(value);
	if (!Number.isFinite(ms)) {return null;}
	return new Date(ms).toISOString();
};

/** Latest of a list of timestamp-ish values, as an ISO string (or null). */
export const latestIsoTimestamp = values => {
	let max = null;
	(values || []).forEach(v => {
		const iso = toIsoTimestamp(v);
		if (iso === null) {return;}
		const t = Date.parse(iso);
		if (max === null || t > max) {max = t;}
	});
	return max === null ? null : new Date(max).toISOString();
};

/**
 * Stamped onto every cached row.  Bump it whenever the *shape* of what we
 * cache changes -- a normaliser gaining or renaming a field, an id scheme
 * change, a new invariant older rows cannot satisfy.
 *
 * Why this exists: with no version stamp, a normaliser change silently mixed
 * old- and new-shaped records inside one collection, and the delta merge then
 * "reconciled" the mismatch by pruning the ones it no longer recognised -- the
 * collection emptied itself and the user saw "No tracking runs found." with
 * nothing to indicate the cache was at fault.  A mismatched stamp now reads as
 * a cache miss, so the next load is a clean full download instead.
 *
 * v4 -- /tracking-runs and /cp-survey-jobs used to answer an internal admin with
 *       only their own token company's records, and those partial lists were
 *       cached together with a delta watermark.  Once the API stopped scoping
 *       admins the delta never re-sent the older runs it had never delivered, so
 *       the cards stayed missing.  Dropping v3 rows forces one clean full
 *       download of every collection.
 * v2 -- `lastSync` canonicalised to ISO-8601 (see toIsoTimestamp); rows written
 *       by v1 may carry an epoch-number watermark, so they are dropped.
 */
export const CACHE_SCHEMA_VERSION = 4;

/** True when a stored row was written by this version of the cache format. */
export const isCurrentSchema = row =>
	!!row && row.schemaVersion === CACHE_SCHEMA_VERSION;

// ── Collection cache ──────────────────────────────────────────────

/**
 * Read a cached collection. Resolves { items, lastSync, savedAt } or undefined.
 */
export const readCollection = key =>
	dbGet(STORE_COLLECTIONS, key).then(row => {
		// A row written by an older schema is deliberately treated as a miss: the
		// caller then does a full download instead of delta-merging against
		// records whose shape it no longer understands (and pruning them).
		if (!isCurrentSchema(row)) {return undefined;}
		return {
			items: row.items || [],
			lastSync: row.lastSync || null,
			savedAt: row.savedAt || null,
			schemaVersion: row.schemaVersion
		};
	});

const writeCollection = (key, items, lastSync) =>
	dbPut(STORE_COLLECTIONS, {
		key,
		items,
		// One canonical watermark type, never an epoch number (see toIsoTimestamp).
		lastSync: toIsoTimestamp(lastSync),
		savedAt: now(),
		schemaVersion: CACHE_SCHEMA_VERSION
	});

/**
 * May a cached collection be served straight from the freshness window,
 * without touching the network at all?
 *
 * Why the emptiness check: an *empty* cached collection used to short-circuit
 * the network too, so once anything had emptied it the cache re-served itself
 * for the whole freshness window and the delta that followed rewrote it --
 * that is the "No tracking runs found." that survived every reload on one
 * machine and never appeared on another.  An empty collection now always
 * revalidates, which makes such a write self-healing on the next load.
 */
export const isServableCachedCollection = (cached, maxAgeMs) => {
	if (!cached || !maxAgeMs) {return false;}
	if (!Array.isArray(cached.items) || cached.items.length === 0) {return false;}
	const age = now() - (cached.savedAt || 0);
	return age >= 0 && age < maxAgeMs;
};

/**
 * Fraction of the known id set that a *non-vouched* `presentIds` is allowed to
 * prune in one pass.  Above it we assume the id list was truncated (a page walk
 * that stopped early, a scoped response, a parse failure) rather than believing
 * that many records really were deleted.
 */
export const PRESENT_IDS_MAX_PRUNE_RATIO = 0.5;

/**
 * Decide whether a delta's `presentIds` may be used to prune the cache.
 *
 * Why this exists: `presentIds` used to be treated as unconditionally
 * authoritative, so every cached item whose id was absent from it was deleted.
 * A response carrying a *partial* id list -- the first page of a walk, a
 * differently-scoped query, a JSON parse failure that degraded to an empty page
 * -- therefore wiped the entire collection while `updated` carried nothing, and
 * the page went blank ("No tracking runs found.").
 *
 * @param {Array}   presentIds  ids the response claims currently exist
 * @param {Array}   knownIds    ids the merge is about to keep (cache + updates)
 * @param {object}  [opts]
 * @param {boolean} [opts.presentIdsComplete] the caller explicitly vouches that
 *        `presentIds` covers the complete scope (e.g. a page walk that actually
 *        reached the last page).  Only then is a large prune believed; `false`
 *        explicitly forbids pruning.
 */
export const canPrunePresentIds = (presentIds, knownIds, { presentIdsComplete } = {}) => {
	if (!Array.isArray(presentIds)) {return false;}
	// An empty id list is indistinguishable from "the response fell over".
	// Deleting everything on that basis is never worth it: a genuinely empty
	// scope is picked up by the next full download instead.
	if (presentIds.length === 0) {return false;}
	// The caller told us this response covered only part of the scope, so the
	// missing ids are simply records it never asked for.
	if (presentIdsComplete === false) {return false;}
	const known = (knownIds || []).map(String);
	if (known.length === 0) {return true;}
	const present = new Set(presentIds.map(String));
	const missing = known.filter(id => !present.has(id)).length;
	if (missing === 0) {return true;}
	// Explicitly complete scope => believe it, however large the deletion.
	if (presentIdsComplete === true) {return true;}
	// No explicit signal: only believe a deletion small enough to look like a
	// real deletion rather than a truncated id list.
	return missing / known.length <= PRESENT_IDS_MAX_PRUNE_RATIO;
};

/**
 * True when a delta merge would empty a collection that *was* populated.
 *
 * That is the exact signature of the desktop-only "No tracking runs found.":
 * the empty merge result was persisted, `savedAt` was refreshed, the freshness
 * window then re-served the empty list, and each later delta rewrote it --
 * permanently, and only on the machine whose IndexedDB took the bad write.
 * Callers must discard such a delta and fall back to a full download.
 */
export const emptiesPopulatedCollection = (merged, cachedItems) =>
	(merged || []).length === 0 && (cachedItems || []).length > 0;

/**
 * Merge a delta response into a cached item list.
 *
 * Deletions can be expressed two ways:
 *   - deletedIds: explicit ids removed since lastSync, or
 *   - presentIds: the complete set of ids that currently exist; any cached
 *                 item whose id is not present may be dropped — but only when
 *                 canPrunePresentIds is satisfied that the list really does
 *                 cover the whole scope.  A partial id list must never be read
 *                 as "everything else was deleted".
 *
 * @param {Array}  cachedItems  existing cached items
 * @param {Array}  updated      created-or-updated items from the delta
 * @param {Array}  deletedIds   ids of records removed since lastSync
 * @param {Array}  presentIds   complete set of currently-existing ids (optional)
 * @param {Function} getId      item => unique id
 * @param {object} [options]    { presentIdsComplete } - see canPrunePresentIds
 */

const mergeDelta = (cachedItems, updated, deletedIds, presentIds, getId, options = {}) => {
	const byId = new Map();
	(cachedItems || []).forEach(item => byId.set(getId(item), item));
	(deletedIds || []).forEach(id => byId.delete(id));
	(updated || []).forEach(item => byId.set(getId(item), item));
	if (canPrunePresentIds(presentIds, [...byId.keys()], options)) {
		const present = new Set(presentIds.map(String));
		[...byId.keys()].forEach(id => {
			if (!present.has(String(id))) {byId.delete(id);}
		});
	}
	return [...byId.values()];
};

/** Normalise fetchFull's return into { items, lastSync }. */
const normalizeFull = result => {
	if (Array.isArray(result)) {return { items: result, lastSync: null };}
	if (result && Array.isArray(result.items)) {
		return { items: result.items, lastSync: result.lastSync || null };
	}
	return { items: [], lastSync: null };
};

/**
 * Cache-first collection fetch with optional delta sync.
 *
 * @param {object}   opts
 * @param {string}   opts.key          user-scoped cache key
 * @param {Function} opts.getId        item => unique id
 * @param {Function} opts.fetchFull    () => Promise<items[] | { items, lastSync }>
 * @param {Function} [opts.fetchDelta] (since) => Promise<{ updated, deleted,
 *                                      presentIds, presentIdsComplete,
 *                                      lastSync }> when supported; return
 *                                      null/throw to fall back to a full
 *                                      download.  Set presentIdsComplete:true
 *                                      only when presentIds provably covers the
 *                                      whole scope (see canPrunePresentIds).
 * @param {Function} [opts.getUpdatedAt] item => timestamp, used to compute
 *                                      lastSync from a full download when the
 *                                      fetcher does not supply one.
 * @param {Function} [opts.onCached]   called with cached items the moment they
 *                                      load (skipped on a cold cache).
 * @param {number}   [opts.maxAgeMs]   if the cache is younger than this, skip
 *                                      the network entirely and serve it as-is.
 * @returns {Promise<Array>} the freshest item list (cached + delta, or full).
 */
export const cachedCollection = ({
	key,
	getId,
	fetchFull,
	fetchDelta,
	getUpdatedAt,
	onCached,
	maxAgeMs
}) =>
	readCollection(key)
		.catch(() => undefined)
		.then(cached => {
			if (cached && typeof onCached === 'function') {
				onCached(cached.items);
			}

			// Fresh-enough cache: serve without hitting the network.  An empty
			// cached collection is never servable - see isServableCachedCollection.
			if (isServableCachedCollection(cached, maxAgeMs)) {
				return cached.items;
			}

			const fullDownload = () =>
				Promise.resolve()
					.then(fetchFull)
					.then(result => {
						const { items, lastSync: fullSync } = normalizeFull(result);
						const lastSync =
							toIsoTimestamp(fullSync) ||
							(getUpdatedAt ? latestIsoTimestamp(items.map(getUpdatedAt)) : null);
						return writeCollection(key, items, lastSync)
							.then(() => items)
							.catch(() => items);
					});

			const canDelta =
				cached && cached.lastSync && typeof fetchDelta === 'function';

			if (!canDelta) {
				return fullDownload();
			}

			return Promise.resolve()
				.then(() => fetchDelta(cached.lastSync))
				.then(delta => {
					if (!delta) {throw new Error('delta-unsupported');}
					const merged = mergeDelta(
						cached.items,
						delta.updated,
						delta.deleted,
						delta.presentIds,
						getId,
						{ presentIdsComplete: delta.presentIdsComplete }
					);
					// A delta must never be able to empty a populated collection: that
					// write is what made "No tracking runs found." permanent on one
					// machine.  Throwing here routes us into fullDownload() below.
					if (emptiesPopulatedCollection(merged, cached.items)) {
						throw new Error('delta-emptied-collection');
					}
					const lastSync =
						toIsoTimestamp(delta.lastSync) ||
						latestIsoTimestamp([
							cached.lastSync,
							...(delta.updated || []).map(getUpdatedAt || (() => null))
						]) ||
						toIsoTimestamp(cached.lastSync);
					return writeCollection(key, merged, lastSync)
						.then(() => merged)
						.catch(() => merged);
				})
				.catch(() => fullDownload());
		});

// ── Query cache (stale-while-revalidate) ──────────────────────────

/**
 * Read a cached query response.  Resolves { value, savedAt } or undefined.
 */
export const readQuery = key =>
	dbGet(STORE_QUERIES, key).then(row =>
		// Rows from an older schema read as a miss, so a normaliser/shape change
		// invalidates them instead of feeding stale shapes to the page.
		(isCurrentSchema(row) ? { value: row.value, savedAt: row.savedAt } : undefined)
	);

export const writeQuery = (key, value) =>
	dbPut(STORE_QUERIES, {
		key,
		value,
		savedAt: now(),
		schemaVersion: CACHE_SCHEMA_VERSION
	});

/**
 * Stale-while-revalidate query fetch.
 *
 * @param {object}   opts
 * @param {string}   opts.key        user-scoped cache key
 * @param {Function} opts.fetcher    () => Promise<value>  network download
 * @param {Function} [opts.onCached] called with the cached value if present
 * @param {number}   [opts.maxAgeMs] if cache younger than this, skip network
 * @returns {Promise<value>} fresh value (or cached value when fresh enough)
 */
export const cachedQuery = ({ key, fetcher, onCached, maxAgeMs }) =>
	readQuery(key)
		.catch(() => undefined)
		.then(cached => {
			if (cached && typeof onCached === 'function') {
				onCached(cached.value);
			}
			if (cached && maxAgeMs) {
				const age = now() - (cached.savedAt || 0);
				if (age >= 0 && age < maxAgeMs) {
					return cached.value;
				}
			}
			return Promise.resolve()
				.then(fetcher)
				.then(value =>
					writeQuery(key, value)
						.then(() => value)
						.catch(() => value)
				);
		});

// ── Maintenance ───────────────────────────────────────────────────

export const invalidateCollection = key => dbDelete(STORE_COLLECTIONS, key);
export const invalidateQuery = key => dbDelete(STORE_QUERIES, key);

/** Drop every cached query whose key starts with prefix (e.g. on filter change). */
export const invalidateQueriesWithPrefix = prefix =>
	dbRowsWithPrefix(STORE_QUERIES, prefix).then(rows =>
		Promise.all(rows.map(row => dbDelete(STORE_QUERIES, row.key)))
	);

/** Wipe the entire cache (call on logout / account switch). */
const clearBrowserCaches = () => {
	if (
		typeof window === 'undefined' ||
		!window.caches ||
		typeof window.caches.keys !== 'function'
	) {
		return Promise.resolve();
	}
	return window.caches
		.keys()
		.then(keys => Promise.all(keys.map(key => window.caches.delete(key))))
		.then(() => undefined)
		.catch(() => undefined);
};

export const clearAllCache = () => {
	clearPageCache();
	return Promise.all([dbClearAll(), clearBrowserCaches()]);
};

/**
 * Console-reachable escape hatch.
 *
 * The guards above make a poisoned cache self-healing on the next load, but a
 * browser that is already stuck (and any future cache bug) needs a way out that
 * does not involve talking a user through DevTools > Application > IndexedDB.
 * Support can now say: open the console and run
 *     await window.corrguardCache.clearAll()
 * There is deliberately no UI for this - it is a diagnostic handle, not a
 * feature.
 */
if (typeof window !== 'undefined') {
	window.corrguardCache = {
		clearAll:            clearAllCache,
		invalidateCollection,
		invalidateQuery,
		invalidateQueriesWithPrefix,
		readCollection,
		cacheKey,
		schemaVersion:       CACHE_SCHEMA_VERSION
	};
}
